// Where a diff's segments are in the two texts it was made from, and its changes' words as they
// read there: without the Markdown that formats them, such as a table's pipes.
import type { Change, DiffSegment } from '@lyrmo/shared';
import { parseMarkdown, readableSlice } from './markdown';

/** Where a diff segment starts: in the original (`before`), and in the rewrite (`after`). */
export interface SegmentPlace {
  readonly before: number;
  readonly after: number;
}

export function segmentPlaces(segments: readonly DiffSegment[]): SegmentPlace[] {
  let before = 0;
  let after = 0;
  return segments.map(({ type, text }) => {
    const place = { before, after };
    if (type !== 'insert') before += text.length;
    if (type !== 'delete') after += text.length;
    return place;
  });
}

/** A change's words, before and after, as they read. */
export interface ReadableChange {
  readonly before: string;
  readonly after: string;
}

/**
 * Each change's words as they read, for the lists of changes. One that only changes formatting,
 * with no words to show for it, keeps its words as they're written.
 */
export function readableChanges(
  original: string,
  rewritten: string,
  segments: readonly DiffSegment[],
  changes: readonly Change[],
): ReadableChange[] {
  const places = segmentPlaces(segments);
  const originalLines = parseMarkdown(original);
  const rewrittenLines = parseMarkdown(rewritten);
  return changes.map((change) => {
    const from = places[change.first] ?? { before: 0, after: 0 };
    const last = segments[change.last];
    const end = places[change.last] ?? from;
    const before = readableSlice(
      original,
      from.before,
      end.before + (last && last.type !== 'insert' ? last.text.length : 0),
      originalLines,
    ).trim();
    const after = readableSlice(
      rewritten,
      from.after,
      end.after + (last && last.type !== 'delete' ? last.text.length : 0),
      rewrittenLines,
    ).trim();
    return before || after ? { before, after } : { before: change.before, after: change.after };
  });
}
